import React, { useMemo, useState } from 'react';
import { EmptyState, ExportBtn, PeriodNote, Section } from './PosBits';
import { PosTable } from './PosTable';
import { inr, monthLabel, num, pctf, usePosGet } from './posUtils';

const G = ({ v }) => (v == null ? null : (
  <span className={`ml-1 text-[10px] ${v >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{v >= 0 ? '▲' : '▼'}{Math.abs(v)}%</span>
));
const nd = (fn) => (v, r) => (r.has_data ? fn(v, r) : <span className="text-slate-400 italic">No data</span>);

const COLS = [
  { key: 'month', label: 'Month', render: (v, r) => <b>{monthLabel(v)}{!r.has_data && <span className="ml-1 text-[10px] text-amber-700">(not imported)</span>}</b> },
  { key: 'unique_customers', label: 'Unique Cust.', align: 'right', render: nd((v, r) => <>{num(v)}<G v={r.unique_customers_growth} /></>) },
  { key: 'total_orders', label: 'Orders', align: 'right', render: nd((v, r) => <>{num(v)}<G v={r.total_orders_growth} /></>) },
  { key: 'total_sales', label: 'Sales', align: 'right', render: nd((v, r) => <>{inr(v)}<G v={r.total_sales_growth} /></>) },
  { key: 'points_earned', label: 'Pts Earned', align: 'right', render: nd(num) },
  { key: 'points_burned', label: 'Pts Burned', align: 'right', render: nd(num) },
  { key: 'net_points', label: 'Net Pts', align: 'right', render: nd(num) },
  { key: 'new_customers', label: 'New', align: 'right', render: nd(num) },
  { key: 'repeat_customers', label: 'Repeat', align: 'right', render: nd(num) },
  { key: 'lost_customers', label: 'Not Returned', align: 'right', render: nd(num) },
  { key: 'retention_rate', label: 'Retention', align: 'right', render: nd(pctf) },
  { key: 'avg_sales_per_customer', label: 'Avg Sales/Cust', align: 'right', render: nd(inr) },
  { key: 'avg_order_value', label: 'AOV', align: 'right', render: nd(inr) },
];

export const MonthwiseTab = ({ params }) => {
  const { data, loading } = usePosGet('monthwise', params);
  const [sort, setSort] = useState('month');
  const [order, setOrder] = useState('asc');
  const rows = useMemo(() => {
    const items = [...(data?.items || [])];
    return items.sort((a, b) => {
      const x = a[sort] ?? -Infinity; const y = b[sort] ?? -Infinity;
      return (x > y ? 1 : x < y ? -1 : 0) * (order === 'asc' ? 1 : -1);
    });
  }, [data, sort, order]);
  if (data?.empty) return <EmptyState />;
  const onSort = (k) => { if (k === sort) setOrder(order === 'asc' ? 'desc' : 'asc'); else { setSort(k); setOrder('desc'); } };
  return (
    <Section title="Monthwise Analysis" testid="monthwise-tab" right={<ExportBtn section="monthwise" params={params} />}>
      <div className="mb-2"><PeriodNote data={data} /></div>
      <PosTable columns={COLS} rows={rows} sort={sort} order={order} onSort={onSort} loading={loading} testid="monthwise-table" />
      <p className="text-[11px] text-slate-500 mt-2">
        Growth arrows compare with the previous imported month. Repeat = present this and previous month; Not Returned = present previous month, absent this month (“Lost” needs the inactivity threshold — see Missing Customers);
        Retention = Repeat ÷ previous-month customers. AOV = Sales ÷ Orders. “No data” means the month was not imported (different from zero).
      </p>
    </Section>
  );
};
